import { readItemField } from './item-field-reader';
import { readItemUuidSlot } from './inventory-raw-readers';
import { isPhpNumeric, phpJsonFloat, roundPhpDecimal } from './php-decimal-values';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { utf8Bytes } from './php-json-serialize';
import { constructFromBaseMagicCatalog, MAGIC_CATALOG_ITEM_ID } from './from-base-magic-catalog';
import type { FromBaseMagicCatalogInputs } from './from-base-magic-catalog';

export interface InventoryDefinitionAssetMetadata {
    meshContractVersion: number;
    paidMeshIds: readonly string[];
    vfxRows: readonly Row[];
    lookSources: Readonly<Record<string, string>>;
    slotDefaults: Readonly<Record<string, string>>;
    slots: readonly Row[];
    allowedOrigins: readonly string[];
    magicCatalog?: FromBaseMagicCatalogInputs;
}
const meshLayers = ['weapon_classification', 'weapon_ammo_classification', 'armor_classification',
    'instrument_classification', 'armor_type', 'lock_type', 'equipment_slot'];
const scalar = (row: Row, key: string, fallback: unknown = null): unknown => readItemField(row, key) ?? fallback;
const text = (row: Row, key: string, fallback = ''): string => String(scalar(row, key, fallback)).trim();
function objectJson(value: unknown, maximum: number): Row | null {
    if (typeof value !== 'string' || value === '' || utf8Bytes(value).length > maximum) return null;
    try { const decoded: unknown = JSON.parse(value); return fromBaseRecord(decoded) ? decoded : null; } catch { return null; }
}
/** ft_hydrate_mesh_rows, over raw SELECT rows. Materialize only validated asset
 * descriptors; malformed presentation data is omitted like the native hydrator. */
export function hydrateInventoryDefinitionMeshes(rows: readonly Row[], metadata: InventoryDefinitionAssetMetadata,
    sizeIds: readonly string[]): Row[] {
    const paid = new Set(metadata.paidMeshIds.map(id => id.toUpperCase())), result: Row[] = [];
    for (const row of [...rows].filter(row => Number(scalar(row, 'mesh_deleted', 0)) === 0
        && Number(scalar(row, 'mesh_unapproved', 0)) === 0).sort((a, b) =>
        text(a, 'mesh_role').localeCompare(text(b, 'mesh_role'))
        || text(a, 'mesh_state_key').localeCompare(text(b, 'mesh_state_key'))
        || text(a, 'mesh_body_profile').localeCompare(text(b, 'mesh_body_profile'))
        || Number(scalar(a, 'mesh_number', 1)) - Number(scalar(b, 'mesh_number', 1))
        || Number(scalar(a, 'mesh_i', 0)) - Number(scalar(b, 'mesh_i', 0)))) {
        const id = text(row, 'mesh_id'), kind = text(row, 'mesh_kind').toLowerCase(), variant = text(row, 'mesh_variant_key'),
            role = text(row, 'mesh_role'), state = text(row, 'mesh_state_key'), profile = text(row, 'mesh_body_profile'),
            quality = text(row, 'mesh_quality_tier').toLowerCase(), url = text(row, 'mesh_url'),
            hash = text(row, 'mesh_sha256').toLowerCase(), order = Math.trunc(phpJsonFloat(scalar(row, 'mesh_number', 1))),
            part = text(row, 'mesh_part_key');
        const token = /^[a-z0-9][a-z0-9_-]{0,63}$/;
        const allowed = url.length <= 1024 && !/[\\\x00-\x20\x7f]/.test(url)
            && (url.startsWith('/') && !url.startsWith('//') || metadata.allowedOrigins.some(origin => url.startsWith(origin)));
        if (!fromBaseUuid(id) || !['glb', 'gltf', 'manifest'].includes(kind) || !token.test(variant) || !token.test(state)
            || !['world', 'inventory', 'equipped', 'projectile', 'effect'].includes(role)
            || !['any', 'masculine_standard', 'masculine_strong', 'feminine_standard', 'feminine_strong'].includes(profile)
            || !['high', 'medium', 'low'].includes(quality) || !allowed || !/^[a-f0-9]{64}$/.test(hash)
            || order < 0 || part !== '' && !/^[a-z0-9][a-z0-9_-]{0,31}$/.test(part)) continue;
        const mesh: Row = { id, kind, variant, role, state, bodyProfile: profile, quality, url, sha256: hash,
            bytes: Math.trunc(phpJsonFloat(scalar(row, 'mesh_bytes', 0))), order, primary: Number(scalar(row, 'primary_mesh', 0)) === 1 };
        if (Number(scalar(row, 'mesh_private', 0)) === 1) mesh.private = true;
        if (paid.has(id.toUpperCase())) mesh.paidSkin = true;
        if (part) mesh.part = part;
        const animation = text(row, 'mesh_animation_set_id'); if (animation) mesh.animationSetId = animation;
        const generation = objectJson(scalar(row, 'mesh_generation_profile'), 32768);
        if (generation) {
            const size = readItemField(generation, 'authoredSizeTypeId');
            if (typeof size === 'string' && sizeIds.includes(size.toUpperCase())) mesh.authoredSizeTypeId = size.toUpperCase();
            const band = generation.presentation;
            if (fromBaseRecord(band) && typeof band.family === 'string' && token.test(band.family)
                && ['quantity', 'fullness'].includes(String(band.axis))
                && Object.keys(band).every(key => ['family', 'axis', 'min', 'max', 'level'].includes(key))) {
                if (band.axis === 'fullness' && Number.isInteger(band.level) && Number(band.level) >= 0 && Number(band.level) <= 4
                    && !('min' in band) && !('max' in band)) mesh.presentation = { family: band.family, axis: band.axis, level: band.level };
                else if (!('level' in band) && ['min', ...('max' in band ? ['max'] : [])].every(key =>
                    isPhpNumeric(readItemField(band, key)) && Number(readItemField(band, key)) >= 0
                    && Number(readItemField(band, key)) <= Number.MAX_SAFE_INTEGER
                    && (band.axis !== 'quantity' || Number.isInteger(Number(readItemField(band, key)))))
                    && (!('max' in band) || Number(band.max) > Number(band.min))) mesh.presentation = { family: band.family,
                        axis: band.axis, min: Number(band.min), ...('max' in band ? { max: Number(band.max) } : {}) };
            }
            const bounds = generation.authoredBoundsM;
            if (Array.isArray(bounds) && bounds.length === 3 && bounds.every(axis => isPhpNumeric(axis) && Number(axis) > 0))
                mesh.boundsM = bounds.map(axis => roundPhpDecimal(Number(axis), 4));
        }
        const calibration = objectJson(scalar(row, 'mesh_calibration'), 8192);
        if (calibration && Object.keys(calibration).length) mesh.calibration = calibration;
        if (utf8Bytes(JSON.stringify(mesh)).length <= 4096) result.push(mesh);
        if (result.length === 64) break;
    }
    return result;
}

/** Published recipe from SELECT metadata when a catalog definition omits its asset surface. */
export function inventoryDefinitionVfxRecipe(id: string, vfxRows: readonly Row[]): Row | null {
    const vfx = vfxRows.find(row => scalar(row, 'ability_vfx_item_id') === id);
    return vfx ? objectJson(scalar(vfx, 'ability_vfx_recipe'), 65536) : null;
}

export function constructInventoryDefinitionAssets(source: Row, raw: (id: string) => Row | null,
    metadata: InventoryDefinitionAssetMetadata, sizeIds: readonly string[], layer: (row: Row, key: string) => unknown,
    audioAssets?: Row): Row {
    const result = fromBaseClone(source), id = String(readItemField(source, 'item_id'));
    const assets: Row = fromBaseRecord(result.assets) ? result.assets : {};
    const recipe = inventoryDefinitionVfxRecipe(id, metadata.vfxRows);
    if (recipe) {
        assets.magic_vfx = recipe;
        if (id.toUpperCase() === MAGIC_CATALOG_ITEM_ID) {
            delete assets.magic_effect;
            const catalog = constructFromBaseMagicCatalog(recipe, metadata.magicCatalog);
            if (catalog) assets.magic_effect = catalog;
        }
    }
    if (audioAssets) Object.assign(assets, audioAssets);
    const meshes = (value: string): Row[] => {
        const source = raw(value);
        if (!source || !Array.isArray(source.meshes)) throw new FromBaseStageMissing('definition-mesh-input', value);
        return hydrateInventoryDefinitionMeshes(source.meshes, metadata, sizeIds);
    };
    let own = meshes(id);
    if (!own.length || own.every(row => row.paidSkin)) {
        let donor = metadata.lookSources[id] ?? '';
        if (!donor) for (const name of meshLayers) {
            const value = readItemUuidSlot(layer(source, name)); if (!value) continue;
            donor = value;
            if (name === 'equipment_slot') {
                const slot = metadata.slots.find(row => readItemField(row, 'item_id') === value);
                const fallback = metadata.slotDefaults[String(readItemField(slot ?? null, 'variable_name'))];
                if (fallback && fallback !== id && !meshes(value).length
                    && ['equipment', 'clothing', 'armor'].includes(String(readItemField(source, 'item_type')))) donor = fallback;
            } else if (metadata.lookSources[value] && !meshes(value).length) donor = metadata.lookSources[value]!;
            break;
        }
        if (donor) own = [...own, ...meshes(donor).map(row => ({ ...row, inheritedFrom: donor }))];
    }
    if (own.length) assets['3d'] = own;
    if (Object.keys(assets).length) result.assets = assets;
    result.computed = { _cache: { meshes_contract_v: metadata.meshContractVersion },
        ...(fromBaseRecord(result.computed) ? result.computed : {}) };
    return result;
}
